import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip } from "chart.js";
import { Box, Stack, Typography } from "@mui/material";

ChartJS.register(ArcElement, ChartTooltip);

/**
 * A status split: donut plus a legend that carries the numbers.
 *
 * `segments` is [{ label, value, color }]. The donut is decoration — every value is also readable
 * as text in the legend, so the panel still works for anyone who cannot separate the arcs by
 * colour, and it degrades to just the legend when `donut` is false.
 */
export default function TutorStatusBreakdown({ segments, donut = true, centreLabel }) {
  const total = segments.reduce((sum, s) => sum + (s.value || 0), 0);

  const legend = (
    <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
      {segments.map((seg) => {
        const value = seg.value || 0;
        const pct = total > 0 ? Math.round((value / total) * 100) : 0;
        return (
          <Box key={seg.label}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: seg.color, flexShrink: 0 }}
              />
              <Typography variant="body2" sx={{ flex: 1, color: "#111827" }} noWrap>
                {seg.label}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {value.toLocaleString()}
              </Typography>
            </Stack>
            {!donut && (
              <Box sx={{ height: 6, borderRadius: 3, bgcolor: "#f1f2f6", mt: 0.75, overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: seg.color }} />
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );

  if (!donut) return legend;

  const chartData = {
    labels: segments.map((s) => s.label),
    datasets: [
      {
        data: segments.map((s) => s.value || 0),
        backgroundColor: segments.map((s) => s.color),
        borderWidth: 0,
      },
    ],
  };

  return (
    <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={2.5} sx={{ width: "100%" }}>
      <Box sx={{ position: "relative", width: 140, height: 140, flexShrink: 0 }}>
        <Doughnut
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: "70%",
            // A donut of all zeros renders as a single grey ring; a tooltip on it would read "0".
            plugins: { legend: { display: false }, tooltip: { enabled: total > 0 } },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: "#111827" }}>
            {total.toLocaleString()}
          </Typography>
          {centreLabel && (
            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
              {centreLabel}
            </Typography>
          )}
        </Box>
      </Box>
      {legend}
    </Stack>
  );
}
